import {
	ABOUT_TALKTALK_BROADBAND,
	TALKTALK_BROADBAND_CONTENT,
	TALKTALK_BROADBAND_IMAGE,
} from "@/data/consts";
import {
	HeaderContent as HeaderContentType,
	HeaderImage as HeaderImageType,
} from "@/types/header";
import { Article as ArticleType, Product } from "@/types/index";
import * as React from "react";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import { getOne } from "@/api/contentful";
import Article from "@/components/Article";
import { getProducts } from "@/api/products";
import Comparison from "@/components/Comparison";
import PageSEO from "@/components/Shared/PageSEO";
import useBroadbandComparison from "@/hooks/useBroadbandComparison";
import PostcodeChecker from "@/components/Comparison/PostcodeChecker";
import { TalkTalkProductSelector } from "@/utils/broadbandProductSelector";
import HeaderImage from "@/components/Header/BroadbandProviders/HeaderImage";
import HeaderContent from "@/components/Header/BroadbandProviders/HeaderContent";
import Head from "next/head";

const Breadcrumbs = [
	{
		title: "Home",
		href: "/",
		active: false,
	},
	{
		title: "Broadband",
		href: "/broadband",
		active: false,
	},
	{
		title: "TalkTalk Broadband Deals",
		href: "/broadband/providers/talktalk",
		active: true,
	},
];

const TalkTalk: React.FC<{
	products: Array<Product>;
	headerContent: HeaderContentType;
	headerImage: HeaderImageType;
	article: ArticleType;
}> = ({ products, headerContent, headerImage, article }) => {
	const broadbandComparison = useBroadbandComparison(products);

	const [sortedFilteredProducts, setSortedFilteredProducts] =
		React.useState<Array<Product>>(products);

	React.useEffect(() => {
		const applyFiltersAndSort = async () => {
			const newSortedFilteredProducts = TalkTalkProductSelector(
				broadbandComparison.products,
				broadbandComparison.contractFilters,
				broadbandComparison.speedFilters,
				broadbandComparison.sorter
			);

			setSortedFilteredProducts(newSortedFilteredProducts);
		};

		applyFiltersAndSort();
	}, [
		broadbandComparison.products,
		broadbandComparison.contractFilters,
		broadbandComparison.speedFilters,
		broadbandComparison.sorter,
	]);

	React.useEffect(() => {
		if (sortedFilteredProducts.length > broadbandComparison.viewableDeals) {
			broadbandComparison.clearFilters();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<Head>
				<link
					rel="canonical"
					href="https://www.savely.co.uk/broadband/providers/talktalk"
				/>
			</Head>
			<PageSEO
				title={"Compare The Latest TalkTalk Broadband Deals | Consumer Choices"}
				description={
					"Compare the latest broadband deals from TalkTalk. Get a great value home broadband deal, or go for a Full Fibre broadband deal and experience speeds of up to 900Mb/s."
				}
				keywords={"TalkTalk Broadband Deals"}
			/>
			<Header
				left={
					<PostcodeChecker
						isloading={broadbandComparison.loading}
						hasResults={broadbandComparison.postcode !== ""}
						setPostcode={broadbandComparison.handlePostcodeChange}
						resultsTitle={
							<div className="row">
								<HeaderImage data={headerImage} />
								<h4 className="postcode_results_title col-sm-9 col-12 mt-3 mt-sm-0">
									{sortedFilteredProducts.length === 0 ? (
										"Sorry, we found no "
									) : (
										<>
											We found {""}
											<span style={{ color: "#d371cc" }}>
												{sortedFilteredProducts.length}
											</span>
										</>
									)}
									{` TalkTalk broadband deals for you in `}
									<span style={{ color: "#d371cc" }}>
										{broadbandComparison.postcode.toUpperCase()}
									</span>
								</h4>
							</div>
						}
						formTitle={
							<div className="row">
								<HeaderImage data={headerImage} />
								<h4 className="postcode_results_title col-sm-9 col-12 mt-3 mt-sm-0">
									Enter your postcode below to discover relevant TalkTalk
									broadband deals based on your location!
								</h4>
							</div>
						}
					/>
				}
				right={<HeaderContent data={headerContent} />}
				breadcrumbs={Breadcrumbs}
			/>
			<Comparison
				products={sortedFilteredProducts}
				hideProvidersFilter={true}
				broadbandComparison={broadbandComparison}
			/>

			<Article article={article} showAuthor={false} showSideBar={false} />
			<Footer />
		</>
	);
};

export async function getStaticProps() {
	const products = await getProducts();
	const headerContent = await getOne(TALKTALK_BROADBAND_CONTENT);
	const headerImage = await getOne(TALKTALK_BROADBAND_IMAGE);
	const article = await getOne(ABOUT_TALKTALK_BROADBAND);

	return {
		props: {
			products: products.data,
			headerContent,
			headerImage,
			article,
		},
		revalidate: Number(process.env.REVALIDATE_CONTENTFUL),
	};
}

export default TalkTalk;
